import { type FC, type ReactNode, lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/auth-store.js';
import { Header } from './components/layout/header.js';
import { Login } from './pages/Login.js';
import { Signup } from './pages/Signup.js';
import { AcceptInvitation } from './pages/AcceptInvitation.js';
import { ForgotPassword, ResetPassword } from './pages/PasswordReset.js';
import { isPublicSignupEnabled } from './lib/features.js';

const UploadPage = lazy(() => import('./pages/UploadPage.js').then((m) => ({ default: m.UploadPage })));
const ContentBrowser = lazy(() => import('./pages/ContentBrowser.js').then((m) => ({ default: m.ContentBrowser })));
const Creators = lazy(() => import('./pages/Creators.js').then((m) => ({ default: m.Creators })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5000, retry: 2 },
  },
});

function PageLoader() {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center p-12">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedContent({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  // Back-button protection: the bfcache / history restore can re-render a
  // protected page from a stale login. Re-evaluate auth state whenever the
  // user returns to this route and on `pageshow` (bfcache restore).
  useEffect(() => {
    const onShow = () => {
      const stillAuthed = useAuthStore.getState().isAuthenticated;
      if (!stillAuthed && !location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, [location.pathname]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />
      <main className="mx-auto max-w-6xl">
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </main>
    </div>
  );
}

export const App: FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Self-signup is closed unless built with VITE_PUBLIC_SIGNUP_ENABLED=true (lib/features.ts). */}
          <Route path="/signup" element={isPublicSignupEnabled() ? <Signup /> : <Navigate to="/login" replace />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <ProtectedContent>
                <UploadPage />
              </ProtectedContent>
            }
          />
          <Route
            path="/content"
            element={
              <ProtectedContent>
                <ContentBrowser />
              </ProtectedContent>
            }
          />
          <Route
            path="/creators"
            element={
              <ProtectedContent>
                <Creators />
              </ProtectedContent>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};
